import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  FilePenLine,
  History,
  Inbox,
  Loader2,
  MessagesSquare,
  PlayCircle,
  Wrench,
} from 'lucide-react';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import { Card } from '../../components/ui/card';
import {
  BarList,
  BigNumber,
  Delta,
  HeadlineTile,
  MetricCard,
  TrendBars,
  percent,
  shortDate,
} from '../../components/metrics/parts';
import { api, type MetricsDashboard } from '../../lib/api';
import { cn } from '../../lib/utils';

/**
 * How well Memory is working, week by week.
 *
 * Every figure comes from one request (`GET /metrics/dashboard`) computed by `buildMetricsDashboard`
 * in @repo/shared-types, so the card definitions live in one place and the "How it's measured" text
 * here only has to describe them. Weeks run Monday to Sunday in the browser's time zone.
 */

const browserZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

function weekLabel(start: string, end: string): string {
  const s = new Date(`${start}T00:00:00Z`);
  const e = new Date(`${end}T00:00:00Z`);
  const month = (d: Date) => new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(d);
  return s.getUTCMonth() === e.getUTCMonth()
    ? `${month(s)} ${s.getUTCDate()} – ${e.getUTCDate()}`
    : `${month(s)} ${s.getUTCDate()} – ${month(e)} ${e.getUTCDate()}`;
}

const monthName = (ym: string, style: 'short' | 'long' = 'short') =>
  new Intl.DateTimeFormat('en-US', { month: style, timeZone: 'UTC' }).format(new Date(`${ym}-01T00:00:00Z`));

export default function AdminMetrics() {
  const timeZone = useMemo(browserZone, []);
  const [week, setWeek] = useState<string | undefined>(undefined);
  const [data, setData] = useState<MetricsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.metrics.dashboard({ week, timeZone }));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [week, timeZone]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      <AdminSidebar />
      <div className="pl-56 flex-1 h-full overflow-y-auto">
        <div className="max-w-5xl mx-auto px-7 py-7">
          <div className="flex items-start gap-4 mb-6">
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-ink">Metrics</h1>
              <p className="text-xs text-ink-muted mt-1">How well Memory is working, week by week · {timeZone}</p>
            </div>
            {data && (
              <div className="ml-auto flex items-center gap-3">
                {!data.week.isLatest && (
                  <button onClick={() => setWeek(undefined)} className="text-xs font-medium text-accent hover:text-accent-hover">
                    Latest week
                  </button>
                )}
                <div className="flex items-center rounded-md border border-border bg-card">
                  <button
                    onClick={() => setWeek(data.week.previousStart)}
                    className="p-1.5 text-ink-muted hover:text-ink"
                    aria-label="Previous week"
                  >
                    <ChevronLeft size={15} />
                  </button>
                  <span className="px-3 text-xs font-semibold text-ink tabular-nums min-w-[7.5rem] text-center">
                    {weekLabel(data.week.start, data.week.end)}
                  </span>
                  <button
                    onClick={() => data.week.nextStart && setWeek(data.week.nextStart)}
                    disabled={!data.week.nextStart}
                    className="p-1.5 text-ink-muted hover:text-ink disabled:opacity-30"
                    aria-label="Next week"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {error && (
            <Card className="px-4 py-3 text-sm text-danger-text bg-danger-bg border-danger-ring mb-4">{error}</Card>
          )}
          {!data && loading && (
            <p className="flex items-center gap-2 text-sm text-ink-faint">
              <Loader2 size={14} className="animate-spin" /> Loading metrics…
            </p>
          )}
          {data && <Dashboard data={data} loading={loading} />}
        </div>
      </div>
    </div>
  );
}

function Dashboard({ data, loading }: { data: MetricsDashboard; loading: boolean }) {
  const selected = data.weeks.indexOf(data.week.start);
  const a = data.activeAutomations;
  const e = data.editShare;
  const c = data.correctionRate;

  return (
    <div className={cn('space-y-8 transition-opacity', loading && 'opacity-60')}>
      <section>
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint mb-2">Headlines</h2>
        <div className="grid gap-3 md:grid-cols-3">
          <HeadlineTile
            icon={PlayCircle}
            title="Active automations"
            goal="Goal 1"
            value={String(a.value)}
            caption={`running across ${a.people} ${a.people === 1 ? 'person' : 'people'}`}
            delta={<Delta value={a.value} previous={a.previous} better="up" />}
            spark={a.series.map((s) => s.value)}
            selected={selected}
            href="#active-automations"
          />
          <HeadlineTile
            icon={FilePenLine}
            title="Edit share"
            goal="Goal 2"
            value={percent(e.median)}
            caption={e.returned ? `median, ${e.returned} brief${e.returned === 1 ? '' : 's'} edited` : 'no briefs edited this week'}
            delta={<Delta value={e.median} previous={e.previousMedian} better="down" unit=" pt" scale={100} />}
            spark={e.series.map((s) => s.median)}
            selected={selected}
            href="#edit-share"
          />
          <HeadlineTile
            icon={CircleCheck}
            title="Correction rate"
            goal="Goal 3"
            value={percent(c.rate)}
            caption={c.outputs ? `${c.corrected} of ${c.outputs} outputs` : 'no outputs this week'}
            delta={<Delta value={c.rate} previous={c.previousRate} better="down" unit=" pt" scale={100} />}
            spark={c.series.map((s) => s.rate)}
            selected={selected}
            href="#correction-rate"
          />
        </div>
      </section>

      <Goal title="Goal 1" count={3}>
        <ActiveAutomationsCard data={data} selected={selected} />
        <SelfMadeCard data={data} />
        <FeedbackVolumeCard data={data} selected={selected} />
      </Goal>

      <Goal title="Goal 2" count={1}>
        <EditShareCard data={data} selected={selected} />
      </Goal>

      <Goal title="Goal 3" count={3}>
        <CorrectionRateCard data={data} selected={selected} />
        <ReviewVolumePlaceholder />
        <FreshnessCard data={data} />
      </Goal>
    </div>
  );
}

function Goal({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <section>
      <div className="flex items-baseline gap-2 mb-3">
        <h2 className="text-base font-bold text-ink">{title}</h2>
        <span className="text-xs text-ink-faint">{count} metric{count === 1 ? '' : 's'}</span>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

// ─── Goal 1 ─────────────────────────────────────────────────────────────────

function ActiveAutomationsCard({ data, selected }: { data: MetricsDashboard; selected: number }) {
  const a = data.activeAutomations;
  return (
    <MetricCard
      id="active-automations"
      icon={PlayCircle}
      title="Active automations"
      tag="Headline"
      description="Automations each person actually has running."
      cadence="Weekly · per person and in total"
      left={
        <div className="space-y-3">
          <BigNumber value={String(a.value)} unit="running" />
          <div className="flex items-center gap-2 flex-wrap">
            <Delta value={a.value} previous={a.previous} better="up" />
            <span className="text-xs text-ink-muted">across {a.people} {a.people === 1 ? 'person' : 'people'}</span>
          </div>
          <TrendBars values={a.series.map((s) => s.value)} labels={data.weeks} selected={selected} format={String} />
        </div>
      }
      right={
        <div>
          <p className="text-[11px] font-semibold text-ink-muted mb-2">Per person</p>
          <BarList
            rows={a.perPerson.map((p) => ({ key: p.key, name: p.name, value: p.count, display: p.count }))}
            emptyText="No automation completed a run this week."
          />
          {a.idle.length > 0 && <p className="text-[11px] text-ink-faint mt-3">{listNames(a.idle)} {a.idle.length === 1 ? 'has' : 'have'} none running.</p>}
        </div>
      }
      howMeasured={
        <>
          <p>An automation is <strong>active</strong> in a week when it completed at least one run in that week (Monday to Sunday, {data.timeZone}). It is credited to the person who created it.</p>
          <p>"None running" lists people who own an enabled automation that did not complete a run this week.</p>
        </>
      }
    />
  );
}

function SelfMadeCard({ data }: { data: MetricsDashboard }) {
  const s = data.selfMade;
  return (
    <MetricCard
      icon={Wrench}
      title="Self-made automations"
      description="Automations people built themselves, rather than having them made for them."
      cadence={`Monthly · ${monthName(s.month, 'long')}`}
      left={
        <div className="space-y-3">
          <BigNumber value={String(s.allTime)} unit="built, all time" />
          <div className="flex items-center gap-2 flex-wrap">
            <Delta value={s.allTime} previous={s.previousMonthAllTime} better="up" since={monthName(String(previousMonthOf(s.month)), 'long')} />
            <span className="text-xs text-ink-muted">{s.byOrigin.console} in the console · {s.byOrigin.mcp} by asking Claude</span>
          </div>
          <TrendBars
            values={s.series.map((m) => m.cumulative)}
            labels={s.series.map((m) => `${m.month}-01`)}
            titles={s.series.map((m) => monthName(m.month))}
            selected={s.series.length - 1}
            format={String}
          />
        </div>
      }
      right={
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold text-ink-muted">Per person</p>
            <p className="text-[11px] text-ink-faint">Total · new</p>
          </div>
          <BarList
            rows={s.perPerson.map((p) => ({
              key: p.key,
              name: p.name,
              value: p.total,
              display: p.total,
              extra: (
                <span className={cn('w-8 text-center rounded text-[10px] px-1 py-0.5', p.new > 0 ? 'bg-success-bg text-success-text' : 'bg-surface-muted text-ink-faint')}>
                  {p.new > 0 ? `+${p.new}` : '0'}
                </span>
              ),
            }))}
            emptyText="Nobody has built an automation yet."
          />
          <p className="text-[11px] text-ink-faint mt-3">New means built in {monthName(s.month, 'long')}.</p>
        </div>
      }
      howMeasured={
        <>
          <p><strong>Self-made</strong> means created by a person in the console or through an MCP client such as Claude. Automations the Slack bot proposed (and someone approved) and ones a script seeded on someone's behalf are not counted.</p>
          <p>Counts are cumulative to the end of each month. A deleted automation is no longer counted in any month.</p>
        </>
      }
    />
  );
}

function FeedbackVolumeCard({ data, selected }: { data: MetricsDashboard; selected: number }) {
  const f = data.feedbackVolume;
  return (
    <MetricCard
      icon={MessagesSquare}
      title="Feedback volume"
      description="How often people correct or update Memory — through the Slack bot or from Claude."
      cadence="Weekly · per person"
      left={
        <div className="space-y-3">
          <BigNumber value={String(f.total)} unit="corrections and updates" />
          <div className="flex items-center gap-3 flex-wrap">
            <Delta value={f.total} previous={f.previousTotal} better="up" />
            <span className="flex items-center gap-1.5 text-xs text-ink-muted">
              <span className="w-2 h-2 rounded-sm bg-accent" /> {f.corrections} corrections
            </span>
            <span className="flex items-center gap-1.5 text-xs text-ink-muted">
              <span className="w-2 h-2 rounded-sm bg-accent/40" /> {f.updates} updates
            </span>
          </div>
          <TrendBars
            values={f.series.map((s) => s.corrections + s.updates)}
            stacked={f.series.map((s) => s.corrections)}
            labels={data.weeks}
            selected={selected}
            format={String}
          />
          <p className="text-[11px] text-ink-faint">
            {f.byChannel.slack} from Slack · {f.byChannel.mcp} from Claude. Questions, and saves that changed nothing, aren&apos;t counted.
          </p>
        </div>
      }
      right={
        <div>
          <p className="text-[11px] font-semibold text-ink-muted mb-2">Per person</p>
          <BarList
            rows={f.perPerson.map((p) => ({
              key: p.key,
              name: p.name,
              value: p.corrections + p.updates,
              lower: p.corrections,
              display: p.corrections + p.updates,
              note: `${p.corrections} corrections, ${p.updates} updates`,
            }))}
            emptyText="No corrections or updates this week."
          />
          {f.idle.length > 0 && <p className="text-[11px] text-ink-faint mt-3">{listNames(f.idle)} gave none this week.</p>}
        </div>
      }
      howMeasured={
        <>
          <p>Counts what people deliberately sent to Memory — an approved "save this" from the Slack bot, or <code>kb_ingest</code> from Claude — classified by what the ingestion run did with it, read from the knowledge base's own change record. No agent is asked.</p>
          <p>A <strong>correction</strong> replaced what a page said (or the ingestion agent flagged a contradiction); an <strong>update</strong> added to a page that already existed. Saves that only created new pages, or changed nothing, are left out.</p>
        </>
      }
    />
  );
}

// ─── Goal 2 ─────────────────────────────────────────────────────────────────

function EditShareCard({ data, selected }: { data: MetricsDashboard; selected: number }) {
  const e = data.editShare;
  return (
    <MetricCard
      id="edit-share"
      icon={FilePenLine}
      title="Edit share"
      tag="Interim headline"
      description="How much of a brief producers change in its Google Doc."
      cadence="Weekly · median"
      left={
        <div className="space-y-3">
          <BigNumber value={percent(e.median)} unit="changed, median" />
          <div className="flex items-center gap-2 flex-wrap">
            <Delta value={e.median} previous={e.previousMedian} better="down" unit=" pt" scale={100} />
            <span className="text-xs text-ink-muted">
              {e.returned} brief{e.returned === 1 ? '' : 's'} edited · {e.tracked} tracked
            </span>
          </div>
          <TrendBars values={e.series.map((s) => s.median)} labels={data.weeks} selected={selected} format={(v) => percent(v)} />
        </div>
      }
      right={
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold text-ink-muted">Per automation</p>
            <p className="text-[11px] text-ink-faint">Edited · median share</p>
          </div>
          <BarList
            avatars={false}
            max={1}
            rows={e.perAutomation.map((p) => ({
              key: p.automationId,
              name: p.name,
              value: p.median,
              display: percent(p.median),
              extra: <span className="w-14 text-right text-[11px] font-normal text-ink-faint">{p.returned} brief{p.returned === 1 ? '' : 's'}</span>,
            }))}
            emptyText={e.tracked ? 'Nobody edited a brief this week.' : 'No brief filed to a watched Drive folder this week.'}
          />
        </div>
      }
      howMeasured={
        <>
          <p>When an automation files its document as a Google Doc in a Drive folder the sources hub watches, every edit someone makes comes back and is compared with what the automation wrote, paragraph by paragraph. Only the changed and added paragraphs are filed into the knowledge base; the automation's own version never is.</p>
          <p>A brief's <strong>edit share</strong> is how much of its wording people changed in total, counted in words: a one-word fix in a long paragraph is under 1%, a rewritten paragraph roughly that paragraph's share. The figure is the median over briefs edited that week. <strong>Tracked</strong> counts briefs whose Doc came back at all — a Doc in an unwatched folder can never show an edit.</p>
          <p>Recorded since 8 October 2026; earlier weeks have nothing to show.</p>
        </>
      }
    />
  );
}

// ─── Goal 3 ─────────────────────────────────────────────────────────────────

function CorrectionRateCard({ data, selected }: { data: MetricsDashboard; selected: number }) {
  const c = data.correctionRate;
  return (
    <MetricCard
      id="correction-rate"
      icon={CircleCheck}
      title="Correction rate"
      tag="Headline"
      description="How often an automation's output needed correcting or updating."
      cadence="Weekly"
      left={
        <div className="space-y-3">
          <BigNumber value={percent(c.rate)} unit="needed a correction or update" />
          <div className="flex items-center gap-2 flex-wrap">
            <Delta value={c.rate} previous={c.previousRate} better="down" unit=" pt" scale={100} />
            <span className="text-xs text-ink-muted">{c.corrected} of {c.outputs} outputs</span>
          </div>
          <TrendBars values={c.series.map((s) => s.rate)} labels={data.weeks} selected={selected} format={(v) => percent(v)} />
        </div>
      }
      right={
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold text-ink-muted">Outputs delivered</p>
            <p className="text-[11px] text-ink-faint">One square per output</p>
          </div>
          {c.items.length === 0 ? (
            <p className="text-xs text-ink-faint py-2">No output was delivered this week.</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-1">
                {c.items.map((item) => (
                  <span
                    key={item.runId}
                    title={`${item.automation}${item.corrected ? ` — corrected in ${item.via.join(' and ')}` : ''}`}
                    className={cn('w-3 h-3 rounded-[2px]', item.corrected ? 'bg-accent' : 'bg-surface-muted')}
                  />
                ))}
              </div>
              <div className="flex items-center gap-4 mt-3 text-[11px] text-ink-muted">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-accent" /> {c.corrected} corrected or updated</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-surface-muted" /> {c.outputs - c.corrected} weren&apos;t</span>
              </div>
              <p className="text-[11px] text-ink-faint mt-2">
                {c.items.filter((i) => i.via.includes('drive')).length} edited in Google Docs · {c.items.filter((i) => i.via.includes('slack')).length} corrected in the Slack thread
              </p>
            </>
          )}
        </div>
      }
      howMeasured={
        <>
          <p>An <strong>output</strong> is a completed run whose document was posted to Slack or filed to Google Drive. It <strong>needed correcting or updating</strong> when either a person edited its Google Doc, or someone saved a correction or update to Memory through the Slack bot in the thread of the run's Slack post.</p>
          <p>Slack replies count only when they address the bot (a mention, or its DM) and the save was approved; only the first Slack destination's post is linked. Drive edits count from 8 October 2026.</p>
        </>
      }
    />
  );
}

function ReviewVolumePlaceholder() {
  return (
    <Card className="px-5 py-4 border-dashed">
      <div className="flex items-start gap-3">
        <span className="w-8 h-8 rounded-md bg-surface-muted text-ink-faint flex items-center justify-center shrink-0">
          <Inbox size={16} />
        </span>
        <div>
          <h3 className="text-sm font-semibold text-ink">Review volume</h3>
          <p className="text-xs text-ink-muted mt-0.5">How much of Memory still needs a person to check it — by reason and by project.</p>
          <p className="text-[11px] text-ink-faint mt-3">Not measured yet.</p>
        </div>
      </div>
    </Card>
  );
}

function FreshnessCard({ data }: { data: MetricsDashboard }) {
  const f = data.freshness;
  return (
    <MetricCard
      icon={History}
      title="Memory freshness"
      description="How current the material in recurring automations' documents is when they run."
      cadence="Weekly · by cadence"
      left={
        <div className="space-y-3">
          <p className="text-[11px] font-semibold text-ink-muted">Freshest material used, weekly average</p>
          {f.groups.length === 0 ? (
            <p className="text-xs text-ink-faint">No automation runs less often than every three days.</p>
          ) : (
            f.groups.map((g) => (
              <div key={g.cadence} className="rounded-md border border-border-subtle px-3.5 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-ink">{g.label}</span>
                  <span className="text-[11px] text-ink-faint">{g.measured} of {g.runs} runs measured</span>
                </div>
                <div className="flex items-baseline justify-between gap-2 mt-1.5">
                  <span className="flex items-baseline gap-1.5">
                    <span className="text-2xl font-bold text-ink tabular-nums">{g.meanDaysOld ?? '—'}</span>
                    <span className="text-xs text-ink-muted">days old on average</span>
                  </span>
                  <Delta value={g.meanDaysOld} previous={g.previousMeanDaysOld} better="down" unit=" days" digits={1} />
                </div>
                <div className="h-1.5 rounded-sm bg-surface-muted mt-2 overflow-hidden">
                  <div
                    className={cn('h-full', g.meanDaysOld !== null && g.meanDaysOld > g.intervalDays ? 'bg-danger-text' : 'bg-accent')}
                    style={{ width: `${Math.min(((g.meanDaysOld ?? 0) / Math.max(g.intervalDays, 0.1)) * 100, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-[10px] text-ink-faint mt-1">
                  <span>0 days</span>
                  <span>Runs every {g.intervalDays} days</span>
                </div>
              </div>
            ))
          )}
          <p className="text-[11px] text-ink-faint">Daily automations and anything running every three days or more often are excluded ({f.excluded}).</p>
        </div>
      }
      right={
        <div>
          <p className="text-[11px] font-semibold text-ink-muted mb-1.5">Runs that missed the last cycle</p>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-ink tabular-nums">{f.missed.count}</span>
            <span className="text-xs text-ink-muted">of {f.missed.of} measured runs were older than their own interval</span>
          </div>
          <div className="mt-2">
            <Delta value={f.missed.count} previous={f.missed.previousCount} better="down" unit={` run${Math.abs(f.missed.count - f.missed.previousCount) === 1 ? '' : 's'}`} />
          </div>
          <ul className="mt-3 space-y-1.5">
            {f.missed.runs.slice(0, 6).map((r) => (
              <li key={r.runId} className="flex items-center gap-3 rounded-md border border-border-subtle px-3 py-2">
                <History size={13} className="text-danger-text shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium text-ink truncate">{r.automation}</span>
                  <span className="block text-[11px] text-ink-faint">{shortDate(r.date)} · {r.cadence.toLowerCase()}</span>
                </span>
                <span className="text-right">
                  <span className="block text-xs font-semibold text-danger-text tabular-nums">{r.daysOld} days old</span>
                  <span className="block text-[10px] text-ink-faint">Runs every {r.intervalDays} days</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      }
      howMeasured={
        <>
          <p>When a run finishes, Memory records every dated item its tools showed the agent (meeting notes, messages, emails, files, and the sources behind knowledge-base pages) and checks which of them made it into the document. <strong>Days old</strong> is the age of the freshest of those, at the moment the run started.</p>
          <p>A run <strong>missed the last cycle</strong> when even its freshest material is older than the automation's own interval — a weekly brief built on nothing newer than nine days missed the last week. The interval comes from the schedule, or for meeting-triggered automations from the gaps between their own runs. Recorded since 8 October 2026.</p>
          <p>Matching is by wording, so material the agent restated in its own words is not counted; the figures are an upper bound on age.</p>
        </>
      }
    />
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function listNames(names: string[]): string {
  if (names.length <= 2) return names.join(' and ');
  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
}

function previousMonthOf(ym: string): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}
